import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Drawer,
  FileInput,
  Group,
  Menu,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconDots,
  IconFiles,
  IconPlus,
  IconStar,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryFilters } from "../../lib/useQueryFilters";
import {
  ContentCard,
  DataTableShell,
  EmptyState,
  FilterBar,
  LoadingOverlay,
  PageHeader,
  SortableTh,
  StatCard,
  StatusBadge,
  useTableSort,
  useVisibleWindow,
} from "../../components/ui";
import { adminApi, contractsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { contractTermsLabel, formatDate, formatPaise } from "../../lib/format";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { axioTokens } from "../../theme";
import type { CompanyContract, ContractStatus } from "../../types/api";

type HistoryTab = "all" | ContractStatus;

const CONTRACT_HISTORY_TABS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "superseded", label: "Superseded" },
  { value: "archived", label: "Archived" },
] as const;

const CONTRACT_HISTORY_DEFAULT = CONTRACT_HISTORY_TABS[0].value;
const CONTRACT_HISTORY_FILTER_DEFAULTS = { history: CONTRACT_HISTORY_DEFAULT };
const CONTRACT_HISTORY_FILTER_ALLOWED = { history: CONTRACT_HISTORY_TABS.map((item) => item.value) };
const CONTRACT_HISTORY_FILTER_OPTIONS = { allowed: CONTRACT_HISTORY_FILTER_ALLOWED };

const EMPTY_FORM = {
  name: "Standard",
  fee_basis: "flat" as "flat" | "pct_ctc",
  fee_flat_paise: undefined as number | undefined,
  fee_pct_ctc: undefined as number | undefined,
  guarantee_days: 30,
  currency: "INR",
  effective_from: "",
  effective_to: "",
  notes: "",
  is_default: false,
  activate_now: true,
  pdf_file: null as File | null,
};

function FeeDisplay({ row }: { row: CompanyContract }) {
  if (row.fee_basis === "flat") {
    return <span className="moneyAmount">{formatPaise(row.fee_flat_paise ?? 0)}</span>;
  }
  return (
    <Text fw="var(--axio-font-weight-bold)" size="xl" className="tabular" style={{ fontFamily: axioTokens.fontHead, letterSpacing: "var(--axio-letter-spacing-tight)" }}>
      {row.fee_pct_ctc ?? 0}%
      <Text span size="sm" c="dimmed" fw="var(--axio-font-weight-semibold)" ml="var(--axio-unit-4)">
        of CTC
      </Text>
    </Text>
  );
}

export function AdminCompanyContractsPage() {
  const { companyId = "" } = useParams<{ companyId: string }>();
  const queryClient = useQueryClient();
  const historyFilters = useQueryFilters(CONTRACT_HISTORY_FILTER_DEFAULTS, CONTRACT_HISTORY_FILTER_OPTIONS);
  const historyTab = historyFilters.values.history as HistoryTab;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<CompanyContract | null>(null);

  const companiesQuery = useQuery({
    queryKey: ["admin-companies"],
    queryFn: () => adminApi.listCompanies({ page_size: 100 }),
  });

  const contractsQuery = useQuery({
    queryKey: ["company-contracts", companyId],
    queryFn: () => contractsApi.listCompanyContracts(companyId),
    enabled: !!companyId,
  });

  const company = useMemo(
    () => (companiesQuery.data?.items ?? []).find((c) => c.id === companyId),
    [companiesQuery.data, companyId],
  );

  const contracts = contractsQuery.data ?? [];
  const activeContracts = contracts.filter((row) => row.status === "active");
  const defaultContract = activeContracts.find((row) => row.is_default) ?? activeContracts[0] ?? null;

  const filteredHistory =
    historyTab === "all" ? contracts : contracts.filter((row) => row.status === historyTab);

  const { sorted: sortedHistory, sortKey, direction, toggle } = useTableSort(filteredHistory, {
    name: (row) => row.name,
    status: (row) => row.status,
    terms: (row) => contractTermsLabel(row),
    guarantee: (row) => row.guarantee_days,
    effective: (row) => new Date(row.effective_from),
    signedPdf: (row) => row.pdf_filename,
  });
  const historyWindow = useVisibleWindow(sortedHistory, historyTab);

  const contractForm = useForm({ initialValues: EMPTY_FORM });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["company-contracts", companyId] });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingContract(null);
    contractForm.reset();
  };

  const openCreate = () => {
    setEditingContract(null);
    contractForm.setValues({
      ...EMPTY_FORM,
      effective_from: new Date().toISOString().slice(0, 10),
    });
    setDrawerOpen(true);
  };

  const openEdit = (row: CompanyContract) => {
    setEditingContract(row);
    contractForm.setValues({
      name: row.name,
      fee_basis: row.fee_basis,
      fee_flat_paise: row.fee_flat_paise ?? undefined,
      fee_pct_ctc: row.fee_pct_ctc ?? undefined,
      guarantee_days: row.guarantee_days,
      currency: row.currency,
      effective_from: row.effective_from,
      effective_to: row.effective_to ?? "",
      notes: row.notes ?? "",
      is_default: row.is_default,
      activate_now: row.status === "active",
      pdf_file: null,
    });
    setDrawerOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: async () =>
      contractsApi.createCompanyContract(companyId, {
        ...contractForm.values,
        effective_to: contractForm.values.effective_to || undefined,
        notes: contractForm.values.notes || undefined,
        pdf_file: contractForm.values.pdf_file || undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Saved", message: "Rate card created." });
      invalidate();
      closeDrawer();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof Error ? err.message : "Something went wrong",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingContract) throw new Error("Select contract to edit");
      return contractsApi.updateContract(editingContract.id, {
        ...contractForm.values,
        effective_to: contractForm.values.effective_to || undefined,
        notes: contractForm.values.notes || undefined,
        pdf_file: contractForm.values.pdf_file || undefined,
      });
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Updated", message: "Rate card updated." });
      invalidate();
      closeDrawer();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof Error ? err.message : "Something went wrong",
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (contractId: string) => contractsApi.activateContract(contractId),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Activated", message: "Rate card is now active." });
      invalidate();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof ApiError ? err.message : "Could not activate",
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (contractId: string) => contractsApi.archiveContract(contractId),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Archived", message: "Contract archived." });
      invalidate();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof ApiError ? err.message : "Could not archive",
      });
    },
  });

  const downloadDraft = async (id: string) => {
    try {
      await contractsApi.downloadContractDraftPdf(id);
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Failed",
        message: error instanceof Error ? error.message : "Download failed",
      });
    }
  };

  const downloadSigned = async (id: string) => {
    try {
      await contractsApi.downloadContractPdf(id);
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Failed",
        message: error instanceof Error ? error.message : "Download failed",
      });
    }
  };

  if (companiesQuery.isLoading || contractsQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-360)" />;
  }

  const companyName = company?.display_name ?? "Company";

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.rateCards}
        title={companyName}
        subtitle="Rate cards and signed PDFs"
        breadcrumb={
          <Anchor component={Link} to="/admin/companies/clients" size="sm" c="dimmed">
            ← {NAV_COPY.admin.backToClients}
          </Anchor>
        }
        actions={
          <Button color="violet" leftSection={<IconPlus size="var(--axio-unit-16)" />} onClick={openCreate}>
            New rate card
          </Button>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard label="Active" value={activeContracts.length} icon={IconCheck} color="green" />
        <StatCard label="Default" value={defaultContract?.name ?? "—"} icon={IconStar} />
        <StatCard label="Total" value={contracts.length} icon={IconFiles} color="gray" />
      </SimpleGrid>

      <Stack gap="sm">
        <Text fw="var(--axio-font-weight-semibold)" size="sm">
          Active rate cards
        </Text>
        {activeContracts.length === 0 ? (
          <ContentCard>
            <EmptyState
              icon={NAV_ICONS.rateCards}
              title="No active rate cards"
              description="Create and activate a rate card so HR can publish jobs."
              action={
                <Button color="violet" leftSection={<IconPlus size="var(--axio-unit-16)" />} onClick={openCreate}>
                  New rate card
                </Button>
              }
            />
          </ContentCard>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {activeContracts.map((row) => (
              <ContentCard key={row.id}>
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <div>
                      <Text fw="var(--axio-font-weight-semibold)">{row.name}</Text>
                      <Group gap="var(--axio-unit-6)" mt="var(--axio-unit-6)">
                        {row.is_default && (
                          <Badge size="sm" variant="outline" color="gray">
                            Default
                          </Badge>
                        )}
                        <StatusBadge status={row.status} />
                      </Group>
                    </div>
                  </Group>

                  <div>
                    <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)" mb="var(--axio-unit-4)">
                      Fee per hire
                    </Text>
                    <FeeDisplay row={row} />
                  </div>

                  <SimpleGrid cols={2} spacing="sm">
                    <div>
                      <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                        Guarantee
                      </Text>
                      <Text size="sm" className="tabular" mt="var(--axio-unit-2)">
                        {row.guarantee_days} days
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                        Effective
                      </Text>
                      <Text size="sm" className="tabular" mt="var(--axio-unit-2)">
                        {formatDate(row.effective_from)}
                        {row.effective_to ? ` – ${formatDate(row.effective_to)}` : " – Open"}
                      </Text>
                    </div>
                  </SimpleGrid>

                  <Group gap="xs" mt="xs">
                    <Button size="xs" variant="default" onClick={() => void downloadDraft(row.id)}>
                      Draft PDF
                    </Button>
                    <Button
                      size="xs"
                      variant="default"
                      disabled={!row.pdf_filename}
                      onClick={() => void downloadSigned(row.id)}
                    >
                      Signed PDF
                    </Button>
                    <Button size="xs" variant="default" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                  </Group>
                </Stack>
              </ContentCard>
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <Stack gap="sm">
        <Text fw="var(--axio-font-weight-semibold)" size="sm">
          Full history
        </Text>
        <FilterBar>
          <Tabs value={historyTab} onChange={(value) => value && historyFilters.set({ history: value })}>
            <Tabs.List>
              {CONTRACT_HISTORY_TABS.map((item) => (
                <Tabs.Tab key={item.value} value={item.value}>
                  {item.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        </FilterBar>

        <DataTableShell
          loading={contractsQuery.isFetching && !contractsQuery.isLoading}
          error={
            contractsQuery.isError
              ? contractsQuery.error instanceof Error
                ? contractsQuery.error.message
                : undefined
              : undefined
          }
          onRetry={() => void contractsQuery.refetch()}
          hasMore={historyWindow.hasMore}
          loadingMore={historyWindow.loadingMore}
          onLoadMore={historyWindow.loadMore}
          isEmpty={filteredHistory.length === 0}
          empty={{
            icon: NAV_ICONS.rateCards,
            title: "No contracts in this filter",
            description: "Try another tab, or create a new rate card.",
          }}
        >
          <Table verticalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableTh columnKey="name" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Name
                </SortableTh>
                <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Status
                </SortableTh>
                <SortableTh columnKey="terms" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Terms
                </SortableTh>
                <SortableTh columnKey="guarantee" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Guarantee
                </SortableTh>
                <SortableTh columnKey="effective" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Effective
                </SortableTh>
                <SortableTh columnKey="signedPdf" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Signed PDF
                </SortableTh>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {historyWindow.items.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="sm" fw="var(--axio-font-weight-medium)">
                      {row.name}
                    </Text>
                    {row.is_default && (
                      <Badge size="xs" variant="outline" color="gray" mt="var(--axio-unit-4)">
                        Default
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={row.status} />
                  </Table.Td>
                  <Table.Td className="tabular">{contractTermsLabel(row)}</Table.Td>
                  <Table.Td className="tabular">{row.guarantee_days} days</Table.Td>
                  <Table.Td className="tabular">
                    {formatDate(row.effective_from)}
                    {row.effective_to ? ` – ${formatDate(row.effective_to)}` : ""}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={row.pdf_filename ? undefined : "dimmed"}>
                      {row.pdf_filename ?? "Not uploaded"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu shadow="md" width={axioTokens.unit[180]} position="bottom-end">
                      <Menu.Target>
                        <Button size="xs" variant="default" px="var(--axio-unit-8)">
                          <IconDots size="var(--axio-unit-16)" />
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => openEdit(row)}>Edit</Menu.Item>
                        <Menu.Item onClick={() => void downloadDraft(row.id)}>Draft PDF</Menu.Item>
                        <Menu.Item disabled={!row.pdf_filename} onClick={() => void downloadSigned(row.id)}>
                          Signed PDF
                        </Menu.Item>
                        {row.status !== "active" && (
                          <Menu.Item onClick={() => activateMutation.mutate(row.id)}>Activate</Menu.Item>
                        )}
                        {row.status !== "archived" && (
                          <Menu.Item color="red" onClick={() => archiveMutation.mutate(row.id)}>
                            Archive
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </DataTableShell>
      </Stack>

      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        position="right"
        size="md"
        title={editingContract ? "Edit rate card" : "New rate card"}
        padding="lg"
      >
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label="Name"
              placeholder="e.g. Junior — ₹10k flat"
              required
              {...contractForm.getInputProps("name")}
            />
            <Select
              label="Fee basis"
              data={[
                { value: "flat", label: "Flat fee per hire" },
                { value: "pct_ctc", label: "% of CTC" },
              ]}
              {...contractForm.getInputProps("fee_basis")}
            />
            <NumberInput label="Guarantee days" min={0} max={365} {...contractForm.getInputProps("guarantee_days")} />
            {contractForm.values.fee_basis === "flat" ? (
              <NumberInput
                label="Fee per hire (paise)"
                min={0}
                thousandSeparator=","
                {...contractForm.getInputProps("fee_flat_paise")}
              />
            ) : (
              <NumberInput label="Fee (% of CTC)" min={0} max={100} {...contractForm.getInputProps("fee_pct_ctc")} />
            )}
            <TextInput label="Currency" {...contractForm.getInputProps("currency")} />
            <TextInput type="date" label="Effective from" {...contractForm.getInputProps("effective_from")} />
            <TextInput type="date" label="Effective to (optional)" {...contractForm.getInputProps("effective_to")} />
          </SimpleGrid>
          <Checkbox
            label="Default for new jobs"
            description="HR job posting pre-selects this rate card when multiple are active"
            {...contractForm.getInputProps("is_default", { type: "checkbox" })}
          />
          <Textarea label="Notes" minRows={2} {...contractForm.getInputProps("notes")} />
          <Alert color="blue" variant="light">
            You can keep multiple rate cards active (e.g. Junior flat + Senior % CTC). Draft PDF is generated from these
            terms. Upload only the final signed PDF here.
          </Alert>
          <FileInput
            label="Signed contract PDF (optional now, upload later)"
            accept="application/pdf"
            {...contractForm.getInputProps("pdf_file")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button
              color="violet"
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={() => {
                if (editingContract) updateMutation.mutate();
                else createMutation.mutate();
              }}
            >
              {editingContract ? "Save changes" : "Create rate card"}
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </Stack>
  );
}
